'use client';

import dynamic from 'next/dynamic';
import type { FC } from 'react';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';

import { useShouldAdSlotBeVisible } from '../../hooks/useShouldAdSlotBeVisible';

export type SearchAdServerIntegrationProps = {
  /**
   * Type of ad slot to display
   * - 'skyscraper': Vertical ad on the right side (desktop only)
   * - 'banner': Horizontal banner in product list (responsive)
   */
  slotType: 'skyscraper' | 'banner';
  /**
   * DOM mount point ID (e.g., 'div-ad-server-mba-serp')
   */
  mountPointId: string;
  /**
   * Major product group for a search result, used to determine if ads should be displayed
   */
  majorMkz?: number;
};

const SearchAdServerIntegrationDoubleClick = dynamic(
  () =>
    import(
      /* webpackChunkName: 'SearchAdServerIntegration.doubleClick' */ './SearchAdServerIntegration.doubleClick'
    ),
  { ssr: false },
);

// Lazy-load the container-only skyscraper layout (containerOnly tenants, e.g. quelle.de / lascana.at)
// to align with the deferred loading of the doubleClick integration (baur.de) and keep it off the
// initial render and hydration path, reducing main-thread work during page load.
const SearchAdServerIntegrationSkyscraperLayout = dynamic(
  () =>
    import(
      /* webpackChunkName: 'SearchAdServerIntegration.skyscraper' */ './SearchAdServerIntegration.skyscraper.layout'
    ).then((mod) => mod.SearchAdServerIntegrationSkyscraperLayout),
  { ssr: false },
);

/**
 * Display Google AdServer ads related to OttoMediaGroup (OMG)
 * Supports both desktop skyscraper ads and responsive banner ads in product list
 * */
export const SearchAdServerIntegration: FC<SearchAdServerIntegrationProps> = ({
  slotType,
  majorMkz,
  mountPointId,
}) => {
  const {
    search: { adServer },
  } = useConfig();

  const { mode } = adServer;

  const showAds = useShouldAdSlotBeVisible(slotType, majorMkz);

  if (!showAds) return null;

  if (slotType === 'skyscraper' && mode === 'containerOnly') {
    return <SearchAdServerIntegrationSkyscraperLayout mountPointId={mountPointId} />;
  }

  // Get ad server integration properties (only available in doubleClick mode)
  if (mode !== 'doubleClick') return null;

  const { adServerClientId, slots } = adServer;

  // Select slot config based on type and device
  const slotConfig = slots[slotType];

  if (!slotConfig) return null;

  return (
    <SearchAdServerIntegrationDoubleClick
      slotType={slotType}
      mountPointId={mountPointId}
      adServerClientId={adServerClientId}
      adUnitName={slotConfig.adUnitName}
      adSizes={slotConfig.adSizes}
    />
  );
};
